/* eslint-disable react-hooks/exhaustive-deps */

import { Box, ThemeProvider, createTheme } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

import { TOAST_MESSAGE_SEVERITY_ERROR } from '../../../layout/types';
import { getEmailTemplateSummaries } from 'modules/emailTemplates/store/emailTemplate.actions';

import {
  getAuthClientPolicies,
  getAuthDateSummary,
  getAuthorizationSummary,
  getCommunicationInfo,
  getDepartureAddress,
  getDestinationAddress,
  getEmployeeInfo,
  getFamilyMembers,
  getFamilyPets,
  getServices,
  getTeam,
  getUserSettings,
  setAuthId,
  setLoadingComplete,
  getAddress,
  getAuthTripsByAuthId
} from '../../store/actions/authorizations.actions';

import {
  getAuthDestinationServices,
  getHouseHuntingList,
  getMortgageLeads,
  getOfferHistoryList,
  sendMortgageLeads,
  updateAuthDestinationServicesBuyer,
  updateMortgageLeads
} from 'modules/authorizations/store/actions/destinationService.actions';

import { getBrokerAgents, getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';

import {
  SEND_MORTGAGE_LEADS_FAILURE,
  UPDATE_AUTH_DESTINATION_SERVICES_BUYER_FAILURE,
  UPDATE_MORTGAGE_LEADS_FAILURE
} from 'modules/authorizations/store/types/destinationServiceTypes';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import {
  fetchAuthDestinationDetails,
  fetchAuthDetails,
  fetchBrokerAgents,
  fetchEnumerations,
  fetchVendors
} from 'modules/authorizations/utils/fetchDataDetailsService';
import { showToast } from 'modules/layout/layout.actions';
import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { getAgents } from 'modules/common/common.actions';
import { getClientDetails, setClientId } from 'modules/clients/clients.actions';
import BuyerServices from './destinationServicesBuyer.component';

const styles = theme => ({
  wideControl: {
    minWidth: 174
  },
  section: {
    marginBottom: theme.spacing(2)
  },
  selfServicePolicySelect: {
    minWidth: 300
  },
  card: {
    width: '100%',
    maxWidth: 1500,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  dialogIconRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '40px',
    paddingBottom: '20px'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  chipClickable: {
    cursor: 'pointer'
  },
  iconOverlayImg: {
    position: 'absolute'
  },
  spacingX: {
    marginRight: theme.spacing(1)
  },
  footer: {
    height: 15,
    marginBottom: theme.spacing(2)
  },
  mb2: {
    marginBottom: '4px',
    marginRight: '16px'
  },
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important'
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important'
  },
  editModalInput: {
    minWidth: '332px'
  },
  dialogContentContainer: {
    margin: '15px'
  },
  labelText: {
    minWidth: 350,
    marginBottom: 0
  }
});
const SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID = '83BBE794-E171-403E-B545-23508B017354';

const DestinationServicesBuyerView = props => {
  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const details = props.authorizationDetails ? props.authorizationDetails[props.match.params.id] : null;
  const [isLoading, setIsLoading] = useState(false);
  const [mortgageLead, setMortgageLead] = useState(null);
  const updateDestinationServiceBuyer = async values => {
    setIsLoading(true);
    const resp = await props.updateAuthDestinationServicesBuyer(props.match.params.id, values);
    if (resp.type === UPDATE_AUTH_DESTINATION_SERVICES_BUYER_FAILURE) {
      props.showToast('Failed to update auth destination services info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    } else {
      setIsLoading(false);
      return true;
    }
  };

  useEffect(() => {
    async function fetchData() {
      const id = props.match.params.id;

      setIsLoading(true);
      props.setAuthId(id);

      await fetchAuthDetails(details, id, props);
      await fetchVendors(props?.vendors, props);
      await fetchBrokerAgents(props?.brokerAgents, props);
      await fetchAuthDestinationDetails(details, id, props);
      if (!props.agents || props.agents.length === 0) {
        await props.getAgents();
      }
      if (!props.templates || props.templates.isLoaded === false) {
        await props.getEmailTemplateSummaries();
      }
      await fetchEnumerations(props);
      if (!mortgageLead) {
        const mortgageLeadResp = await props.getMortgageLeads(id);
        if (mortgageLeadResp?.response) {
          setMortgageLead(mortgageLeadResp.response);
        }
      }
      setIsLoading(false);
      props.setLoadingComplete();
    }

    fetchData();
  }, []);

  const updateMortgageLeads = async (fileId, values) => {
    const resp = await props.updateMortgageLeads(fileId, values);
    if (resp.type === UPDATE_MORTGAGE_LEADS_FAILURE) {
      props.showToast('Failed to update mortgage leads info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    } else {
      const mortgageLeadResp = await props.getMortgageLeads(fileId);
      setMortgageLead(mortgageLeadResp?.response);
      setIsLoading(false);
      return true;
    }
  };

  const sendMortgageLeads = async fileId => {
    const resp = await props.sendMortgageLeads(fileId);
    if (resp.type === SEND_MORTGAGE_LEADS_FAILURE) {
      props.showToast('Failed to send mortgage leads info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    } else {
      const mortgageLeadResp = await props.getMortgageLeads(fileId);
      setMortgageLead(mortgageLeadResp?.response);
      setIsLoading(false);
      return true;
    }
  };

  const canReadDestination = hasPermission(Permissions.File.Destination.Read);
  const canEditDestination = hasPermission(Permissions.File.Destination.Edit);
  const isShortTermAssignment = details?.authorizationDetails?.moveTypeId?.toUpperCase() === SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID;
  const staEditAccess = hasPermission(Permissions.File.STA.Edit);

  if (!canReadDestination) {
    props.history.push('/');
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && details && details.destinationServices && (
          <BuyerServices
            updateDestinationService={updateDestinationServiceBuyer}
            details={details}
            brokerAgents={props.agents}
            id={props.match.params.id}
            history={props.history}
            location={props.location}
            enumerations={props.enumerations.allEnums}
            addresses={details.addresses}
            emailTemplates={props.templates ? props.templates.emailTemplates : []}
            vendors={props.vendors}
            clientInfo={props.clientsInfo[details.authorizationDetails.clientId]}
            mortgageLead={mortgageLead}
            updateMortgageLeads={updateMortgageLeads}
            sendMortgageLeads={sendMortgageLeads}
            isReadOnly={isShortTermAssignment ? !staEditAccess : !canEditDestination}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

DestinationServicesBuyerView.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { authorizationDetails, isLoading, vendors, brokerAgents } = state.authorizations;
  const { unmappedClients, clientsInfo } = state.clients;
  const enumerations = state.enumerations;
  const { agents } = state.common;
  const templates = state.templates;

  return {
    authorizationDetails,
    unmappedClients,
    isLoading,
    vendors,
    brokerAgents,
    enumerations,
    agents,
    templates,
    clientsInfo
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    getAuthorizationSummary,
    getAuthDateSummary,
    getAuthDestinationServices,
    updateAuthDestinationServicesBuyer,
    getBrokerAgents,
    setAuthId,
    setLoadingComplete,
    getHouseHuntingList,
    getOfferHistoryList,
    getEmployeeInfo,
    getCommunicationInfo,
    getAuthClientPolicies,
    getDepartureAddress,
    getDestinationAddress,
    getTeam,
    getServices,
    getUserSettings,
    getVendors,
    getFamilyMembers,
    getFamilyPets,
    getAddress,
    getAuthTripsByAuthId,
    showToast,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getAgents,
    getClientDetails,
    setClientId,
    getEmailTemplateSummaries,
    updateMortgageLeads,
    getMortgageLeads,
    sendMortgageLeads
  })
)(DestinationServicesBuyerView);
